'use client';

import React, { useState, useMemo } from 'react';
import { ClipboardList, Plus, Download, Printer, CheckCircle, Clock, FileEdit } from 'lucide-react';
import { toast } from 'sonner';
import { MOCK_DAILY_RECORDS, DailyRecord } from './dailyRecordsData';
import DailyRecordForm from './DailyRecordForm';
import DailyRecordsTable from './DailyRecordsTable';
import Icon from '@/components/ui/AppIcon';



export default function DailyRecordsContent() {
  const [records, setRecords] = useState<DailyRecord[]>(MOCK_DAILY_RECORDS);
  const [formOpen, setFormOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<DailyRecord | null>(null);
  const [filterDate, setFilterDate] = useState('');
  const [filterFarm, setFilterFarm] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const filtered = useMemo(() => {
    return records.filter((r) => {
      const matchDate = !filterDate || r.date === filterDate;
      const matchFarm = filterFarm === 'all' || r.farm === filterFarm;
      const matchStatus = filterStatus === 'all' || r.status === filterStatus;
      return matchDate && matchFarm && matchStatus;
    }).sort((a, b) => b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt));
  }, [records, filterDate, filterFarm, filterStatus]);

  function handleSave(data: DailyRecord) {
    if (editRecord) {
      setRecords((prev) => prev.map((r) => r.id === data.id ? data : r));
      toast.success('Daily record updated successfully');
    } else {
      setRecords((prev) => [data, ...prev]);
      toast.success('Daily record saved successfully');
    }
    setFormOpen(false);
    setEditRecord(null);
  }

  function handleEdit(record: DailyRecord) {
    setEditRecord(record);
    setFormOpen(true);
  }

  function handleApprove(id: string) {
    setRecords((prev) => prev.map((r) => r.id === id ? { ...r, status: 'approved' } : r));
    toast.success('Record approved');
  }

  function handleDelete(id: string) {
    setRecords((prev) => prev.filter((r) => r.id !== id));
    toast.success('Record deleted');
  }

  const todayStr = new Date().toISOString().split('T')[0];
  const todayRecords = records.filter((r) => r.date === todayStr);
  const pendingCount = records.filter((r) => r.status === 'submitted').length;
  const approvedToday = records.filter((r) => r.date === todayStr && r.status === 'approved').length;
  const totalBirdsToday = todayRecords.reduce((s, r) => s + r.closingCount, 0);

  return (
    <div className="px-4 md:px-6 xl:px-8 py-5 max-w-screen-2xl mx-auto">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ClipboardList size={20} style={{ color: 'var(--primary)' }} />
            <h1 className="text-2xl font-bold text-foreground">Daily Records</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Fast daily data entry for flock operations, feed, eggs, and environment
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary text-sm py-2 px-3">
            <Printer size={14} />
            <span className="hidden sm:inline">Print</span>
          </button>
          <button className="btn-secondary text-sm py-2 px-3">
            <Download size={14} />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={() => { setEditRecord(null); setFormOpen(true); }}
            className="btn-primary text-sm"
          >
            <Plus size={15} />
            New Record
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          {
            id: 'dr-today',
            label: "Today's Records",
            value: todayRecords.length,
            icon: ClipboardList,
            color: 'var(--primary)',
            bg: '#f0fdf4',
          },
          {
            id: 'dr-pending',
            label: 'Pending Approval',
            value: pendingCount,
            icon: Clock,
            color: 'var(--warning)',
            bg: 'var(--warning-bg)',
          },
          {
            id: 'dr-approved',
            label: 'Approved Today',
            value: approvedToday,
            icon: CheckCircle,
            color: 'var(--success)',
            bg: 'var(--success-bg)',
          },
          {
            id: 'dr-birds',
            label: 'Birds Tracked Today',
            value: totalBirdsToday.toLocaleString(),
            icon: FileEdit,
            color: 'var(--info)',
            bg: 'var(--info-bg)',
          },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.id} className="kpi-card flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.bg }}>
                <Icon size={16} style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">{s.label}</p>
                <p className="text-lg font-bold font-tabular text-foreground">{s.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="card p-3 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">Date</label>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="form-input text-sm py-1.5 px-2 h-8"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">Farm</label>
            <select
              value={filterFarm}
              onChange={(e) => setFilterFarm(e.target.value)}
              className="form-input text-sm py-1.5 px-2 h-8"
            >
              <option value="all">All Farms</option>
              <option value="Idudi Poultry Farm">Idudi Poultry Farm</option>
              <option value="Kampala Demo Farm">Kampala Demo Farm</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="form-input text-sm py-1.5 px-2 h-8"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
            </select>
          </div>
          {(filterDate || filterFarm !== 'all' || filterStatus !== 'all') && (
            <button
              onClick={() => { setFilterDate(''); setFilterFarm('all'); setFilterStatus('all'); }}
              className="text-xs text-muted-foreground hover:text-foreground underline"
            >
              Clear filters
            </button>
          )}
          <span className="ml-auto text-xs text-muted-foreground">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Table */}
      <DailyRecordsTable
        records={filtered}
        onEdit={handleEdit}
        onApprove={handleApprove}
        onDelete={handleDelete}
      />

      {/* Form Modal */}
      {formOpen && (
        <DailyRecordForm
          open={formOpen}
          editRecord={editRecord}
          onClose={() => { setFormOpen(false); setEditRecord(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
